/**
 * Pure helpers for the new-test page picker (Paper → Experiments → "01 New
 * test · Pick a page" and "01b New test · From planner").
 */
import { estimateTestDuration, isLowDailyTraffic, type TestType } from "@/lib/utils/abTestStatistics"

/** Planner defaults, used when the user did not come from the planner. */
export const DEFAULT_PLAN = { baseline: 5, mde: 20, significance: 95 } as const

/** Page traffic window for the picker and planner: last 30 days. */
export const PAGE_TRAFFIC_DAYS = 30

/** What the Test Planner hands to /split-testing/new in router state. */
export type PlannerState = {
  fromPlanner?: boolean
  estimatedDailyVisitors?: number
  testTypeContext?: string
  sampleSizePerVariant?: number
  estimatedDuration?: number
  baseline?: number
  mde?: number
  significance?: number
}

const GOAL_PHRASE: Record<TestType, { more: string; noun: string }> = {
  engagement: { more: "More clicks", noun: "clicks" },
  conversion: { more: "More sign-ups", noun: "sign-ups" },
  revenue: { more: "More trials", noun: "trials" },
  funnel: { more: "More completed funnels", noun: "funnels" },
}

const isTestType = (v: unknown): v is TestType =>
  v === "engagement" || v === "conversion" || v === "revenue" || v === "funnel"

/** "about 6 weeks", "about 4 months", "about 5 days" */
export function formatAbout(days: number): string {
  if (days < 14) return `about ${Math.max(1, Math.round(days))} ${Math.round(days) === 1 ? "day" : "days"}`
  if (days <= 90) return `about ${Math.round(days / 7)} weeks`
  const months = Math.round(days / 30)
  return `about ${months} ${months === 1 ? "month" : "months"}`
}

/**
 * The planner banner line, built from the plan's own values:
 * "More trials · spots a 20% lift · 95% sure it is real · about 6 weeks at 644 visitors a day".
 * Parts the plan did not set are left out; null when nothing is known.
 */
export function planSummaryLine(state: PlannerState | null | undefined): string | null {
  if (!state?.fromPlanner) return null
  const goal = isTestType(state.testTypeContext) ? GOAL_PHRASE[state.testTypeContext].more : null
  const parts = [
    goal,
    state.mde ? `spots a ${state.mde}% lift` : null,
    state.significance ? `${state.significance}% sure it is real` : null,
    state.estimatedDuration
      ? `${formatAbout(state.estimatedDuration)}${
          state.estimatedDailyVisitors
            ? ` at ${Math.round(state.estimatedDailyVisitors).toLocaleString("en-US")} visitors a day`
            : ""
        }`
      : null,
  ].filter(Boolean)
  return parts.length ? parts.join(" · ") : null
}

/** `slow`: under 100 visitors a day; the picker warns but still lets the page be picked. */
export type TimeToResult = { label: string; disabled: boolean; slow?: boolean }

/**
 * How long a test on a page with this traffic takes, in words. A page under
 * 100 visitors a day (`isLowDailyTraffic`) is never blocked: a new page about
 * to get ad traffic has none yet, so it reads as slow and stays pickable.
 */
export function timeToResult(
  daily: number,
  plan: { baseline: number; mde: number; significance: number; goalNoun: string },
): TimeToResult {
  const { days } = estimateTestDuration(daily, plan.baseline / 100, plan.mde, plan.significance)
  if (isLowDailyTraffic(daily)) {
    return {
      label: days > 365 ? "Over a year at this traffic" : `Slow: about ${Math.max(1, Math.round(days / 30))} months`,
      disabled: false,
      slow: true,
    }
  }
  if (days <= 60) return { label: `About ${Math.max(1, Math.round(days / 7))} ${Math.round(days / 7) <= 1 ? "week" : "weeks"}`, disabled: false }
  if (days <= 180) return { label: `Slow: about ${Math.round(days / 30)} months`, disabled: false }
  return { label: `Too slow for ${plan.goalNoun}`, disabled: false }
}

export function planForPicker(state: PlannerState | null | undefined) {
  const type = isTestType(state?.testTypeContext) ? state!.testTypeContext as TestType : null
  return {
    baseline: state?.baseline || DEFAULT_PLAN.baseline,
    mde: state?.mde || DEFAULT_PLAN.mde,
    significance: state?.significance || DEFAULT_PLAN.significance,
    goalNoun: type ? GOAL_PHRASE[type].noun : "sign-ups",
  }
}

/** "/" → "Homepage", "/product/workflows" → "Product · Workflows" */
export function pageTitle(path: string): string {
  const clean = path.split(/[?#]/)[0].replace(/\/+$/, "")
  if (!clean || clean === "/") return "Homepage"
  return clean
    .split("/")
    .filter(Boolean)
    .map((seg) => {
      const words = decodeURIComponent(seg).replace(/[-_]+/g, " ").trim()
      return words.charAt(0).toUpperCase() + words.slice(1)
    })
    .join(" · ")
}

/** One-character mark for the row, like Paper's "/", "$", "◐", "✎". */
export function pageGlyph(path: string): string {
  const p = path.toLowerCase()
  if (p === "/" || p === "") return "/"
  if (/pric|checkout|plans|billing/.test(p)) return "$"
  if (/blog|post|article|news|guide/.test(p)) return "✎"
  return "◐"
}

export type PickerPage = {
  path: string
  title: string
  glyph: string
  daily: number
  time: TimeToResult
}

/** Busiest pages first, at most `limit`. Rows without a path or traffic drop out. */
export function buildPickerPages(
  rows: Array<{ page?: string; unique_visitors?: number }> | null | undefined,
  plan: ReturnType<typeof planForPicker>,
  limit = 8,
): PickerPage[] {
  return (rows ?? [])
    .filter((r) => r.page && (r.unique_visitors ?? 0) > 0)
    .map((r) => ({ path: r.page as string, daily: Math.max(1, Math.round((r.unique_visitors as number) / PAGE_TRAFFIC_DAYS)) }))
    .sort((a, b) => b.daily - a.daily)
    .slice(0, limit)
    .map(({ path, daily }) => ({
      path,
      title: pageTitle(path),
      glyph: pageGlyph(path),
      daily,
      time: timeToResult(daily, plan),
    }))
}

/** Longest selector accepted from a link; anything longer is not a real CSS path. */
const MAX_SELECTOR_LENGTH = 500

/**
 * Deep-link params for /split-testing/new. Heatmaps' "Test this element" sends
 * `?page=<url>&element=<selector>`: the page to test and the element the
 * editor should start on. Blank values read as absent.
 */
export function parseTestDeepLink(search: string | URLSearchParams): {
  page: string | null
  element: string | null
} {
  const params = typeof search === "string" ? new URLSearchParams(search) : search
  const page = params.get("page")?.trim() || null
  const raw = params.get("element")?.trim() || null
  const element = raw && raw.length <= MAX_SELECTOR_LENGTH ? raw : null
  return { page, element }
}
